import { RiskScoreRevealType } from '@/api/generated-catch-backend/model';
import { useGetRiskScoreRevealByTypeV1RiskScoreRevealRevealTypeGet } from '@/api/generated-catch-backend/risk-score-reveal/risk-score-reveal';
import { ROUTE_DASHBOARD } from '@/constants/routes';
import { useFullScreen } from '@/queries/layout.queries';
import { motion } from 'motion/react';
import { useCallback, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { RiskScoreRevealFrame } from './risk-score-reveal-frame';
import './risk-score-reveal-reset.css';

function RiskScoreLoader() {
  const dotVariants = {
    initial: { scale: 0.8, opacity: 0.5 },
    animate: {
      scale: [0.8, 1.2, 0.8],
      opacity: [0.5, 1, 0.5],
    },
  };

  const containerVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.15,
        repeat: Infinity,
        repeatDelay: 0.3,
      },
    },
  };

  return (
    <div
      data-testid="risk-score-reveal-page-loader"
      className="flex h-screen w-screen flex-col items-center justify-center bg-white"
    >
      <div className="mb-4 text-2xl font-semibold text-gray-900">Preparing your risk score reveal</div>
      <motion.div
        className="flex items-center justify-center gap-1"
        variants={containerVariants}
        initial="initial"
        animate="animate"
      >
        {[0, 1, 2].map((index) => (
          <motion.div
            key={index}
            className="h-2 w-2 rounded-full bg-gray-900"
            variants={dotVariants}
            transition={{
              duration: 0.8,
              ease: 'easeInOut',
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          />
        ))}
      </motion.div>
    </div>
  );
}

export default function RiskScoreRevealPage() {
  useFullScreen();
  const navigate = useNavigate();
  const [frameIdx, setFrameIdx] = useState(0);

  const riskScoreRevealResult = useGetRiskScoreRevealByTypeV1RiskScoreRevealRevealTypeGet(
    RiskScoreRevealType['cancer-reveal'],
    {
      query: {
        retry: 3,
      },
    },
  );

  const frames = riskScoreRevealResult.data?.frames;

  const onNext = useCallback(() => {
    if (!frames) return;

    if (frameIdx === frames.length - 1) {
      navigate(ROUTE_DASHBOARD);
      return;
    }
    setFrameIdx((curr) => curr + 1);
  }, [frames, frameIdx, navigate]);

  if (riskScoreRevealResult.isLoading) {
    return <RiskScoreLoader />;
  }

  if (riskScoreRevealResult.isError) {
    return <Navigate to={ROUTE_DASHBOARD} replace />;
  }

  const frame = frames?.[frameIdx];
  if (!frame) return; // Shouldn't happen because we early return for loading or errors but it makes TS happy

  return (
    <div data-testid="risk-score-reveal-page" className="risk-score-reveal-reset h-screen w-screen bg-white">
      <RiskScoreRevealFrame
        frame={frame}
        onNext={onNext}
        buttonText="Next"
        data-testid={`risk-score-reveal-frame-${frameIdx}`}
      />
    </div>
  );
}
